import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthApiService } from '../api/auth-api.service';
import { AuthTokenResponse, CurrentUserResponse } from '../api/auth-api.types';

type StoredSession = {
  accessToken: string;
  expiresAtUtc: string;
  user: CurrentUserResponse;
};

const SESSION_STORAGE_KEY = 'ideashub.auth.session';
const DEMO_ROLE_STORAGE_KEY = 'ideashub.demo.role';
export type DemoRoleCode = 'EMPLOYEE' | 'MODERATOR' | 'COMMITTEE';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private session: StoredSession | null = this.loadSession();
  private demoRoleOverride: DemoRoleCode | null = this.loadDemoRoleOverride();

  constructor(
    private readonly authApi: AuthApiService,
    private readonly router: Router
  ) {}

  get isAuthenticated(): boolean {
    return this.session !== null;
  }

  get accessToken(): string | null {
    return this.session?.accessToken ?? null;
  }

  get currentUser(): CurrentUserResponse | null {
    const user = this.session?.user ?? null;
    if (!user) return null;
    if (!this.demoRoleOverride) return user;
    return {
      ...user,
      roles: [this.demoRoleOverride]
    };
  }

  /** Roles from the last token/refresh (ignores dev demo-role override). */
  get backendRoles(): string[] {
    const roles = this.session?.user.roles;
    if (!roles?.length) {
      return [];
    }
    return roles.map((r) => r.toUpperCase());
  }

  get isAdmin(): boolean {
    return this.backendRoles.includes('ADMIN');
  }

  get selectedDemoRole(): DemoRoleCode | null {
    return this.demoRoleOverride;
  }

  setDemoRoleOverride(role: DemoRoleCode | null): void {
    this.demoRoleOverride = role;
    if (role) {
      localStorage.setItem(DEMO_ROLE_STORAGE_KEY, role);
      return;
    }
    localStorage.removeItem(DEMO_ROLE_STORAGE_KEY);
  }

  exchangeGoogleToken(idToken: string): Observable<CurrentUserResponse> {
    return this.authApi.exchangeGoogleToken({ idToken }).pipe(
      tap((response) => this.setSession(response)),
      map((response) => response.user)
    );
  }

  refreshCurrentUser(): Observable<CurrentUserResponse | null> {
    if (!this.session) {
      return of(null);
    }

    return this.authApi.getCurrentUser().pipe(
      tap((user) => this.updateUser(user)),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.logout();
          return of(null);
        }

        return of(this.session?.user ?? null);
      })
    );
  }

  logout(redirectToLogin = true): void {
    this.session = null;
    localStorage.removeItem(SESSION_STORAGE_KEY);

    if (redirectToLogin) {
      void this.router.navigate(['/login']);
    }
  }

  private setSession(response: AuthTokenResponse): void {
    const session: StoredSession = {
      accessToken: response.accessToken,
      expiresAtUtc: response.expiresAtUtc,
      user: response.user
    };

    this.session = session;
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  }

  private updateUser(user: CurrentUserResponse): void {
    if (!this.session) {
      return;
    }

    this.session = {
      ...this.session,
      user
    };

    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.session));
  }

  private loadSession(): StoredSession | null {
    const rawSession = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!rawSession) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawSession) as Partial<StoredSession>;
      if (!parsed.accessToken || !parsed.expiresAtUtc || !parsed.user) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }

      if (Date.parse(parsed.expiresAtUtc) <= Date.now()) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }

      return {
        accessToken: parsed.accessToken,
        expiresAtUtc: parsed.expiresAtUtc,
        user: parsed.user
      };
    } catch {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
  }

  private loadDemoRoleOverride(): DemoRoleCode | null {
    const raw = localStorage.getItem(DEMO_ROLE_STORAGE_KEY);
    if (!raw) return null;
    const normalized = raw.trim().toUpperCase();
    if (normalized === 'EMPLOYEE' || normalized === 'MODERATOR' || normalized === 'COMMITTEE') {
      return normalized;
    }
    localStorage.removeItem(DEMO_ROLE_STORAGE_KEY);
    return null;
  }
}
